import { Box, Button, IconButton, Spacer, Stack, Tag, Text } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatBytes, formatSeconds } from "lib/utils";
import { formatContentTime } from "lib/content";
import { MdDragIndicator, MdPlaylistAdd, MdPlaylistRemove } from "react-icons/md";
import VideoPlayer from "views/admin/content/components/video-player";
import Thumbnail from "views/admin/content/components/thumbnail";
import { matchesResolutions, resolutionKey } from "lib/resolution";
import { ContentTypeIcon, NEW_ENTRY_BG, NEW_ENTRY_TITLE, SelectionNumber, UsedDot } from "./common";

// These components live at module level on purpose: if they were defined inside
// ContentSelection they would be a new component type on every render, and React
// would re-create the whole list (scroll jumps to top, Ctrl+F matches are lost).

// `usedIn`: the regular broadcasts that play this content (green dot); only the list of
// available content passes it
function Title({ item, usedIn }) {
  return (
    <>
      <Box flexShrink={0}><Thumbnail content={item} /></Box>
      <ContentTypeIcon type={item.type} />
      <UsedDot count={usedIn ? usedIn.length : 0} names={usedIn ? usedIn.map(b => b.name) : undefined} />
      <Text fontSize='sm' fontWeight='medium'>{item.displayName || item.filename}</Text>
      <VideoPlayer content={item} trigger={<Button colorScheme='brand' variant='outline' size='xs'>View</Button>} />
    </>
  )
}

// `resolutions`: the resolutions the broadcast is made for. A video or picture that is not one of
// them is shown in orange (it is listed only when "Show all content" is ticked, or it was added before).
function Meta({ item, resolutions }) {
  const resolution = resolutionKey(item)
  const limited = Array.isArray(resolutions) && resolutions.length > 0
  const mismatch = limited && !matchesResolutions(item, resolutions)
  return (
    <Stack direction='row' alignItems='center' w='100%' wrap='wrap' gap={2} spacing={0}>
      <Text fontSize='sm' color='gray'>{formatContentTime(item) || "No timestamp"}</Text>
      {resolution && <Tag fontSize='xs' variant='subtle' colorScheme={mismatch ? 'orange' : 'gray'} title={mismatch ? 'Not one of the resolutions of this broadcast' : undefined}>{resolution}</Tag>}
      {!resolution && mismatch && <Tag fontSize='xs' variant='subtle' colorScheme='orange'>resolution unknown</Tag>}
      <Text fontSize='xs' variant='subtle' color='red.600'>{item.size ? formatBytes(item.size) : 'N/A'}</Text>
      <Text fontSize='xs' variant='subtle' color='blue.600'>{formatSeconds(item.duration || 0)}</Text>
    </Stack>
  )
}

export function AvailableContentItem({ item, count, onAdd, usedIn, resolutions }) {
  return (
    <Box bg={count > 0 ? 'green.50' : 'white'} w='100%' borderWidth='1px' borderRadius='lg' p={4}>
      <Stack direction='column' alignItems='start' w='100%'>
        <Stack direction='row' alignItems='center' w='100%' wrap='wrap' gap={2} spacing={0}>
          <Title item={item} usedIn={usedIn} />
          <Spacer />
          {count > 0 && <Tag fontSize='xs' variant='subtle' colorScheme='green'>x {count}</Tag>}
          <IconButton aria-label='Add' size='xs' variant='outline' icon={<MdPlaylistAdd />} colorScheme='brand' onClick={() => onAdd(item.id)} />
        </Stack>
        <Meta item={item} resolutions={resolutions} />
      </Stack>
    </Box>
  )
}

export function SelectedContentItem({ item, position, onRemove, resolutions }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.sortKey });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, position: 'relative', zIndex: isDragging ? 1 : 0 };

  return (
    <div ref={setNodeRef} style={style}>
      <Box bg={item.isNew ? NEW_ENTRY_BG : 'white'} title={item.isNew ? NEW_ENTRY_TITLE : undefined} w='100%' borderWidth='1px' borderRadius='lg' p={4}>
        <Stack direction='column' alignItems='start' w='100%'>
          <Stack direction='row' alignItems='center' w='100%' wrap='wrap' gap={2} spacing={0}>
            <MdDragIndicator {...listeners} {...attributes} style={{ cursor: 'grab', flexShrink: 0 }} />
            <SelectionNumber value={position} />
            {item.missing
              ? <Text fontSize='sm' color='red.500'>Content no longer exists</Text>
              : <Title item={item} />}
            <Spacer />
            <IconButton aria-label='Remove' size='xs' variant='outline' icon={<MdPlaylistRemove />} colorScheme='red' onClick={() => onRemove(position - 1)} />
          </Stack>
          {!item.missing && <Meta item={item} resolutions={resolutions} />}
        </Stack>
      </Box>
    </div>
  )
}
